import { Device, Call } from '@twilio/voice-sdk';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { getPreferredMicId, getPreferredSpeakerId } from './audioConfig';

import type { QueueNext } from './types';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * TwilioEngine — the CRM's dial engine. Wraps the Twilio
 * Voice JS SDK: fetch an Access Token from our server, build a Twilio.Device,
 * and place/track outbound calls. Kept deliberately small and framework-free so
 * PhoneContext drives it through a small imperative surface, and so it's
 * unit-testable without React.
 *
 * State is surfaced through callbacks (onState/onError) rather than events, to
 * match how PhoneContext consumes call lifecycle.
 */
export type TwilioCallState = 'idle' | 'connecting' | 'ringing' | 'active' | 'ended';

export interface TwilioEngineCallbacks {
  onState?: (state: TwilioCallState) => void;
  onError?: (message: string) => void;
  onReady?: () => void;
  /** The device lost its registration and the engine is getting it back. */
  onReconnecting?: () => void;
  /** Recovery keeps failing — the UI should say so and offer a manual reconnect.
   *  The engine keeps retrying in the background (backoff capped at 60s). */
  onDisconnected?: () => void;
  /** Fetches a Twilio access token. Defaults to our server; injectable for tests. */
  getToken?: () => Promise<string>;
  // Fired when an inbound call arrives. `from` is the caller's number. The
  // consumer decides whether to accept() or reject().
  onIncoming?: (from: string, accept: () => void, reject: () => void) => void;
}

/**
 * Twilio error codes meaning "the access token is no longer good". After
 * 31205 the SDK STOPS trying to register (`_stopRegistrationTimer`), so the
 * device stays dead until someone hands it a new token — which, before this,
 * only a logout + login did.
 */
const TOKEN_ERROR_CODES = new Set([20101, 20104, 31204, 31205]);

/** Server tokens live 1h (telephonyTwilio.ts). Renew well before that when a
 *  health check notices an old one — the SDK's own tokenWillExpire is a
 *  setTimeout, which a sleeping laptop or a throttled background tab misses. */
export const TOKEN_MAX_AGE_MS = 45 * 60_000;

/** Retry delays for recovery; the last one repeats. */
export const RECOVERY_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

/** Failed attempts before the UI is told the phone is disconnected. */
const ATTEMPTS_BEFORE_DISCONNECTED = 3;

/** Caps on a token request and on building + registering a device, so a
 *  hung request (captive Wi-Fi portal, stalled socket) cannot freeze recovery. */
const TOKEN_TIMEOUT_MS = 10_000;
const REGISTER_TIMEOUT_MS = 20_000;

/** A live call blocks a rebuild — not a failure, just "later". */
const DEFERRED = new Error('deferred: call in progress');
/** Safety net for a deferred rebuild, in case the call's end event is missed. */
const DEFERRED_RETRY_MS = 15_000;

const withTimeout = <T,>(p: Promise<T>, ms: number, what: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${what} timed out`)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });

export class TwilioEngine {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private cb: TwilioEngineCallbacks;
  private tokenFetchedAt = 0;
  private recovering: Promise<void> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private failedAttempts = 0;
  private destroyed = false;
  // Set once the UI was told "disconnected"; background retries then stay
  // quiet instead of flipping it back to "reconnecting" on every attempt.
  private reportedDisconnected = false;

  constructor(cb: TwilioEngineCallbacks = {}) {
    this.cb = cb;
  }

  private async fetchToken(): Promise<string> {
    const request = this.cb.getToken
      ? this.cb.getToken()
      : axios.get(`${API_URL}/api/telephony/twilio/token`, { headers: auth() }).then((r) => r.data?.token as string);
    const token = await withTimeout(request, TOKEN_TIMEOUT_MS, 'Twilio token');
    if (!token) throw new Error('No Twilio token');
    this.tokenFetchedAt = Date.now();
    return token;
  }

  private isRegistered(): boolean {
    return (this.device as any)?.state === 'registered';
  }

  /** How many callers are waiting in the inbound queue. */
  /** Inbound queue: how many are waiting, and who is next (server-resolved). */
  static async queueStatus(): Promise<{ waiting: number; next: QueueNext | null }> {
    try {
      const r = await axios.get(`${API_URL}/api/telephony/twilio/queue-depth`, { headers: auth() });
      return { waiting: Number(r.data?.waiting) || 0, next: r.data?.next || null };
    } catch {
      return { waiting: 0, next: null };
    }
  }

  /** The caller I claimed from the queue, and whether they've been bridged
   *  into my conference yet. Polled after a pull goes 'active' (= MY leg
   *  joined) until `bridged` — the caller arrives a moment later. `ended`
   *  means the server already finalized that row (caller hung up in transit). */
  static async pulled(): Promise<(QueueNext & { bridged: boolean; ended: boolean }) | null> {
    try {
      const r = await axios.get(`${API_URL}/api/telephony/twilio/pulled`, { headers: auth() });
      return r.data?.pulled || null;
    } catch {
      return null;
    }
  }

  /** Whether the browser + config can run Twilio Voice. */
  static async isConfigured(): Promise<boolean> {
    try {
      const r = await axios.get(`${API_URL}/api/telephony/twilio/token`, { headers: auth() });
      return !!r.data?.token;
    } catch {
      return false;
    }
  }

  /** Initialise the Twilio Device from a freshly-minted access token. */
  async init(): Promise<void> {
    // Runs under the same one-at-a-time guard as recover(): the engine is
    // exposed before init() finishes, so a focus/online/dial health check can
    // fire meanwhile — and two concurrent builds would destroy each other.
    const run = (async () => {
      const token = await this.fetchToken();
      await this.buildDevice(token);
    })();
    this.recovering = run;
    try {
      await run;
    } finally {
      if (this.recovering === run) this.recovering = null;
    }
  }

  /**
   * Create, wire and register a Device, replacing (and destroying) the current
   * one. This is what logging out and in used to be the only way to do, and it
   * is the only reliable recovery: the SDK's register() works only from
   * 'unregistered', and after an expired-token error a device can be left
   * stuck in 'registering' for good.
   */
  private async buildDevice(token: string): Promise<void> {
    const old = this.device;
    const device = new Device(token, {
      // Opus first for the browser↔Twilio leg (PSTN leg is still G.711).
      codecPreferences: ['opus', 'pcmu'] as any,
    });
    this.device = device;
    if (old) { try { old.destroy(); } catch { /* already gone */ } }
    // Events from a device that has since been replaced are ignored.
    const current = () => this.device === device && !this.destroyed;

    device.on('error', (e: any) => {
      if (!current()) return;
      this.cb.onError?.(e?.message || 'Twilio device error');
      // An expired/invalid token leaves the device permanently unregistered.
      if (TOKEN_ERROR_CODES.has(Number(e?.code))) void this.recover();
    });
    device.on('registered', () => {
      if (!current()) return;
      this.failedAttempts = 0;
      this.reportedDisconnected = false;
      this.cb.onReady?.();
    });
    // Lost the registration (signaling gave up, token expired…). The SDK only
    // re-registers by itself after a signaling reconnect with a VALID token.
    device.on('unregistered', () => { if (current()) void this.recover(); });
    // Inbound call: surface it so PhoneContext can show the IncomingCallCard.
    device.on('incoming', (call: Call) => {
      if (!current()) return;
      const from = (call.parameters?.From as string) || '';
      this.cb.onIncoming?.(
        from,
        () => { this.activeCall = call; this.wireCall(call); call.accept(); },
        () => { try { call.reject(); } catch { /* noop */ } },
      );
    });
    // Renew the token before it expires. A failed renewal used to be
    // swallowed, leaving the token to expire; recovery retries it.
    device.on('tokenWillExpire', () => { if (current()) void this.recover(); });

    await withTimeout(device.register(), REGISTER_TIMEOUT_MS, 'Twilio register');
    // Honour the rep's chosen mic + speaker (the same persisted deviceIds the
    // path uses). Without this, Twilio picks the SYSTEM DEFAULT mic — the
    // laptop's, not the headset — so a rep on AirPods isn't heard when they move
    // away from the laptop. Best-effort and deliberately NOT awaited: it can
    // trigger a mic-permission prompt, and a hanging prompt must not keep the
    // engine "not ready" while the UI already shows callers waiting.
    this.applyDevices().catch(() => { /* keep default devices */ });
  }

  /** Apply the persisted mic (+ speaker where supported) to the Twilio Device. */
  async applyDevices(): Promise<void> {
    const audio: any = this.device?.audio;
    if (!audio) return;
    try {
      const micId = getPreferredMicId();
      if (micId && typeof audio.setInputDevice === 'function') await audio.setInputDevice(micId);
    } catch { /* keep default mic */ }
    try {
      const spkId = getPreferredSpeakerId();
      if (spkId && typeof audio.speakerDevices?.set === 'function') await audio.speakerDevices.set(spkId);
    } catch { /* keep default speaker */ }
  }

  /** Switch the mic live (mid-call too — Twilio applies it to the active call). */
  async setMicDevice(deviceId: string | null): Promise<void> {
    const audio: any = this.device?.audio;
    if (!audio || typeof audio.setInputDevice !== 'function') return;
    try { await audio.setInputDevice(deviceId || 'default'); } catch { /* noop */ }
  }

  /** Place an outbound call to a PSTN number. logId (our CallLog id) is passed
   *  through so the /voice webhook can link the conference recording back. */
  async call(to: string, logId?: string): Promise<void> {
    if (!this.device) throw new Error('Twilio device not ready');
    this.cb.onState?.('connecting');
    // params are forwarded to the /voice webhook as POST fields (To, LogId).
    this.activeCall = await this.device.connect({ params: { To: to, LogId: logId || '' } });
    this.wireCall(this.activeCall);
  }

  private wireCall(call: Call) {
    call.on('ringing', () => this.cb.onState?.('ringing'));
    call.on('accept', () => this.cb.onState?.('active'));
    call.on('disconnect', () => { this.cb.onState?.('ended'); this.onCallEnded(); });
    call.on('cancel', () => { this.cb.onState?.('ended'); this.onCallEnded(); });
    call.on('reject', () => { this.cb.onState?.('ended'); this.onCallEnded(); });
    call.on('error', (e: any) => this.cb.onError?.(e?.message || 'Twilio call error'));
  }

  /** A call ended. If the device went down during it, rebuild NOW rather
   *  than waiting out a backoff — the rep is about to take the next call. */
  private onCallEnded() {
    this.activeCall = null;
    if (!this.destroyed && this.device && !this.isRegistered()) void this.recover();
  }

  mute(on: boolean) { this.activeCall?.mute(on); }
  sendDigits(d: string) { this.activeCall?.sendDigits(d); }

  /** Pull the next waiting caller from the inbound queue (rep answers next).
   *  The /voice webhook joins the rep to their own conference and, once the
   *  rep is confirmed in, redirects the front-of-queue caller into it. */
  async pullQueue(): Promise<void> {
    if (!this.device) throw new Error('Twilio device not ready');
    this.cb.onState?.('connecting');
    this.activeCall = await this.device.connect({ params: { PullQueue: '1' } });
    this.wireCall(this.activeCall);
  }

  /** Hold/unhold the customer leg (server puts them on conference hold). */
  async hold(on: boolean): Promise<void> {
    await axios.post(`${API_URL}/api/telephony/twilio/hold`, { hold: on }, { headers: auth() });
  }

  /** Add a 3rd participant (number) into the active conference. */
  async addParticipant(number: string): Promise<void> {
    await axios.post(`${API_URL}/api/telephony/twilio/add-participant`, { number }, { headers: auth() });
  }

  hangup() {
    try { this.activeCall?.disconnect(); } catch { /* noop */ }
    this.activeCall = null;
  }

  /** The live RTCPeerConnection, so the recorder/stats can attach.
   *  NOTE: reaches into @twilio/voice-sdk private internals — if a version bump
   *  renames these, this returns null and recording/stats/boost silently stop.
   *  We log a warning so that regression is visible rather than invisible. */
  getPeerConnection(): RTCPeerConnection | null {
    const c = this.activeCall as any;
    const pc = c?._mediaHandler?.version?.pc || c?._mediaHandler?.pc
      || c?._mediaHandler?.pc?.version?.pc || null;
    if (this.activeCall && !pc) {
      // eslint-disable-next-line no-console
      console.warn('[twilio] could not locate the RTCPeerConnection — recording/stats/boost disabled. The @twilio/voice-sdk internal shape may have changed.');
    }
    return pc as RTCPeerConnection | null;
  }

  /**
   * Get a fresh token and make sure the device is registered. Safe to call any
   * time and from many places at once (one run at a time). A registered device
   * just gets the new token; anything else — no device yet (the first token
   * request failed), unregistered, or stuck registering — is rebuilt. On
   * failure it schedules its own retry with backoff, so it heals by itself.
   */
  recover(): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    // Join a run in progress (another recovery, or init) rather than racing it.
    // Its failure is handled by whoever started it.
    if (this.recovering) return this.recovering.catch(() => {});
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    if (!this.isRegistered() && !this.reportedDisconnected) this.cb.onReconnecting?.();
    this.recovering = (async () => {
      try {
        const token = await this.fetchToken();
        if (this.destroyed) return;
        if (this.isRegistered()) {
          this.device!.updateToken(token);
        } else if (this.activeCall) {
          // Rebuilding would drop the live call. Hand over the token and try
          // the rebuild again once the call is over.
          try { this.device?.updateToken(token); } catch { /* destroyed */ }
          throw DEFERRED;
        } else {
          await this.buildDevice(token);
        }
        this.failedAttempts = 0;
        this.reportedDisconnected = false;
        // A token-only refresh on a registered device fires no event, so
        // report ready here too.
        this.cb.onReady?.();
      } catch (err) {
        if (this.destroyed) return;
        if (err === DEFERRED) {
          // Not a failure: the rep is talking. The call's end triggers the
          // rebuild (onCallEnded); this timer is only a safety net.
          this.retryTimer = setTimeout(() => { this.retryTimer = null; void this.recover(); }, DEFERRED_RETRY_MS);
          return;
        }
        this.failedAttempts += 1;
        // Only a device that is actually down is "disconnected". A renewal
        // that failed while the device is registered (its token still has
        // minutes left) keeps retrying quietly.
        if (this.failedAttempts >= ATTEMPTS_BEFORE_DISCONNECTED && !this.isRegistered() && !this.reportedDisconnected) {
          this.reportedDisconnected = true;
          this.cb.onDisconnected?.();
        }
        const delay = RECOVERY_BACKOFF_MS[Math.min(this.failedAttempts - 1, RECOVERY_BACKOFF_MS.length - 1)];
        this.retryTimer = setTimeout(() => { this.retryTimer = null; void this.recover(); }, delay);
      } finally {
        this.recovering = null;
      }
    })();
    return this.recovering;
  }

  /**
   * Called on tab focus, network back online, every minute, and on a dial
   * attempt while down. Recovers when there is no device, it is not
   * registered, or the token is getting old — the cases the SDK's own
   * tokenWillExpire timer misses after sleep or in a throttled tab.
   */
  checkHealth(): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    const tokenOld = Date.now() - this.tokenFetchedAt > TOKEN_MAX_AGE_MS;
    if (!this.device || !this.isRegistered() || tokenOld) return this.recover();
    return Promise.resolve();
  }

  destroy() {
    this.destroyed = true;
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    this.hangup();
    try { this.device?.destroy(); } catch { /* noop */ }
    this.device = null;
  }
}
